import BillingsPlanEntryTaxiDriverRecalculateParams, {
  BillingsPlanEntryTaxiDriverRecalculateParamsBillingNode,
  BillingsPlanEntryTaxiDriverRecalculateParamsBonusContribution,
  BillingsPlanEntryTaxiDriverRecalculateParamsContractCorrectionContribution,
  BillingsPlanEntryTaxiDriverRecalculateParamsContribution,
  BillingsPlanEntryTaxiDriverRecalculateParamsContributionType,
  BillingsPlanEntryTaxiDriverRecalculateParamsPenaltyContribution,
} from "../../../../../common/services/billings/plan-entry/taxi-driver/recalculate/billings-plan-entry-taxi-driver-recalculate-params";
import BillingsNode from "../../../common/types/billings-node";
import BillingsTaxiDriverContributionBonus, {
  BillingsTaxiDriverContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-driver-contributions-bonus";
import BillingsTaxiDriverContributionContractCorrection, {
  BillingsTaxiDriverContributionContractCorrectionType,
} from "../../common/contributions/contract-correction/types/billings-taxi-driver-contributions-contract-correction";
import BillingsTaxiDriverContributionPenalty, {
  BillingsTaxiDriverContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-driver-contributions-penalty";
import BillingFormData, {
  BillingFormDataContributions,
  BillingSummaryData,
} from "../types/billing-form.data";

const createBillingBonusContribution = (
  contribution: BillingsTaxiDriverContributionBonus,
):
  | BillingsPlanEntryTaxiDriverRecalculateParamsBonusContribution
  | undefined => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance ?? 0,
        rate: contribution.rate ?? 0,
      };
    case BillingsTaxiDriverContributionBonusType.BONUS_OTHER:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiDriverContributionPenalty,
):
  | BillingsPlanEntryTaxiDriverRecalculateParamsPenaltyContribution
  | undefined => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_BEING_LATE:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.PENALTY_BEING_LATE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.PENALTY_INCOMPATIBLE_CAR,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_OTHER:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.PENALTY_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_WRONG_ROUTE:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.PENALTY_WRONG_ROUTE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingContractCorrectionContribution = (
  contribution: BillingsTaxiDriverContributionContractCorrection,
):
  | BillingsPlanEntryTaxiDriverRecalculateParamsContractCorrectionContribution
  | undefined => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionContractCorrectionType.BASE_AMOUNT_EXTERNAL_TAXI:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.BASE_AMOUNT_EXTERNAL_TAXI,
        distance: contribution.distance ?? 0,
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionContractCorrectionType.CONTRACT_CORRECTION_RATE:
      return {
        type: BillingsPlanEntryTaxiDriverRecalculateParamsContributionType.CONTRACT_CORRECTION_RATE,
        rate: contribution.rate ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsPlanEntryTaxiDriverRecalculateParamsContribution[] => {
  const bonusContributions: BillingsPlanEntryTaxiDriverRecalculateParamsBonusContribution[] =
    contributions.bonus
      .filter((contribution) => contribution.type !== null)
      .map((contribution) => createBillingBonusContribution(contribution)!);

  const penaltyContributions: BillingsPlanEntryTaxiDriverRecalculateParamsPenaltyContribution[] =
    contributions.penalty
      .filter((contribution) => contribution.type !== null)
      .map((contribution) => createBillingPenaltyContribution(contribution)!);

  const contractCorrectionContributions: BillingsPlanEntryTaxiDriverRecalculateParamsContractCorrectionContribution[] =
    contributions.contractCorrection
      .filter((contribution) => contribution.type !== null)
      .map(
        (contribution) =>
          createBillingContractCorrectionContribution(contribution)!,
      );

  return [
    ...bonusContributions,
    ...penaltyContributions,
    ...contractCorrectionContributions,
  ];
};

const createBillingNode = (
  node: BillingsNode,
): BillingsPlanEntryTaxiDriverRecalculateParamsBillingNode => {
  return {
    allowCharge: node.isHighwayAllowed,
    checkoutDate: node.checkoutDate,
    checkoutEventId: node.checkoutEventUuid,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.isEditable,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    id: node.id,
    lat: node.lat,
    lon: node.lon,
    nodeId: node.nodeId,
    orderId: node.orderId,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
  };
};

const createBillingNodes = (
  nodes: BillingsNode[],
): BillingsPlanEntryTaxiDriverRecalculateParamsBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createRecalculateParams = (
  data: BillingFormData,
  planEntryId: string,
): BillingsPlanEntryTaxiDriverRecalculateParams => {
  return {
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
    planEntryId,
  };
};

const billingRecalculateRequestFactory = {
  createRecalculateParams,
};

export default billingRecalculateRequestFactory;
